import { GlobalPolygon, HistoryEntry } from '../types';

export const LS_URL  = 'ors_pg_baseUrl';
export const LS_HIST = 'ors_pg_history';
export const LS_GLOBAL_POLYGONS = 'ors_pg_global_polygons';
export const HIST_MAX = 15;
export const MAX_GLOBAL_POLYGONS = 10;

export function loadHistory(): HistoryEntry[] {
  try { return JSON.parse(localStorage.getItem(LS_HIST) || '[]'); }
  catch { return []; }
}

export function saveHistory(h: HistoryEntry[]) {
  let arr = h.slice(0, HIST_MAX);
  if (arr.length === 0) {
    localStorage.setItem(LS_HIST, '[]');
    return;
  }
  while (arr.length > 0) {
    try {
      localStorage.setItem(LS_HIST, JSON.stringify(arr));
      return;
    } catch {
      arr = arr.slice(0, Math.max(1, arr.length - 5));
    }
  }
}

export function lsUsage(): number {
  let total = 0;
  for (const k in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, k)) {
      total += ((localStorage as any)[k]?.length || 0) + k.length;
    }
  }
  return total * 2;
}

// ── Global polygon library (persisted to localStorage) ────────────────────────

export function loadGlobalPolygons(): GlobalPolygon[] {
  try { return JSON.parse(localStorage.getItem(LS_GLOBAL_POLYGONS) || '[]'); }
  catch { return []; }
}

export function saveGlobalPolygons(polygons: GlobalPolygon[]): void {
  try { localStorage.setItem(LS_GLOBAL_POLYGONS, JSON.stringify(polygons)); } catch { /* quota */ }
}

// Slim down a response so history fits comfortably in localStorage.
export function sliceResponseForHistory(resJson: any) {
  return {
    bbox: resJson.bbox,
    metadata: resJson.metadata ? { query: { units: resJson.metadata?.query?.units } } : undefined,
    routes: (resJson.routes || []).map((r: any) => {
      // ORS instruction types:
      //   0 Left, 1 Right, 2 Sharp L, 3 Sharp R, 4 Slight L, 5 Slight R,
      //   6 Continue, 7 Enter roundabout, 8 Exit roundabout, 9 U-turn,
      //   10 Finish, 11 Depart, 12 Keep left, 13 Keep right
      const segments = r.segments || [];
      let turnCount = 0, roundabouts = 0, keeps = 0, uturns = 0;
      let totalAvgSpeed: number | null = null, segCount = 0, totalDetour: number | null = null;
      let stepCount = 0, stepAvgSpeed: number | null = null, stepDetour: number | null = null;
      for (const seg of segments) {
        segCount++;
        if (seg.avgspeed != null) totalAvgSpeed = (totalAvgSpeed || 0) + seg.avgspeed;
        if (seg.detourfactor != null) totalDetour = (totalDetour || 0) + seg.detourfactor;
        for (const step of (seg.steps || [])) {
          stepCount++;
          const t = step.type;
          if (t >= 0 && t <= 5) turnCount++;
          else if (t === 7) roundabouts++;
          else if (t === 9) uturns++;
          else if (t === 12 || t === 13) keeps++;
          if (step.avgspeed != null) stepAvgSpeed = (stepAvgSpeed || 0) + step.avgspeed;
          if (step.detourfactor != null) stepDetour = (stepDetour || 0) + step.detourfactor;
        }
      }
      return {
        geometry: r.geometry,
        summary: r.summary,
        way_points: r.way_points,
        extras: r.extras ? { tollways: r.extras.tollways } : undefined,
        _stats: {
          turnCount, roundabouts, keeps, uturns,
          avgSpeed: totalAvgSpeed != null ? +(totalAvgSpeed / segCount).toFixed(1)
            : stepAvgSpeed != null ? +(stepAvgSpeed / stepCount).toFixed(1) : null,
          detourFactor: totalDetour != null ? +(totalDetour / segCount).toFixed(2)
            : stepDetour != null ? +(stepDetour / stepCount).toFixed(2) : null,
        },
      };
    }),
  };
}

